import React, { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { ScrollArea } from "./ui/scroll-area";
import { Checkbox } from "./ui/checkbox"; // Added for multi-select facilities
import AdminRegistrationForm from "./AdminRegistrationForm";
import {
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  Building2,
} from "lucide-react";
import Papa from "papaparse";
import { Badge } from "./ui/badge";

const BulkEmployeeImport = ({
  onSubmit,
  facilities = [],
  categories = [],
  supabase,
}) => {
  const [tab, setTab] = useState("single");
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]); // Changed to array for multiple facilities
  const [user] = useState(JSON.parse(localStorage.getItem("user")));

  const validCategoryValues = useMemo(() => {
    const categoryMap = new Map();
    categories.forEach((category) => {
      categoryMap.set(
        category.name,
        new Set(category.custom_category_options.map((opt) => opt.value))
      );
    });
    return categoryMap;
  }, [categories]);

  const generateSampleCSV = () => {
    const headers = [
      "first_name",
      "last_name",
      "email",
      "position",
      ...categories.map((cat) => cat.name),
    ];

    const sampleData = [
      "Jan",
      "Novak",
      "jan.novak@example.com",
      "Sales Representative",
      ...categories.map((cat) => cat.custom_category_options[0]?.value || ""),
    ];

    const csvContent = [headers.join(","), sampleData.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employee_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const validateEmployeeData = (data) => {
    const errors = [];
    const warnings = [];

    if (selectedFacilities.length === 0) {
      errors.push("Please select at least one facility before importing");
      return { errors, warnings };
    }

    data.forEach((row, index) => {
      // Validate required fields
      if (!row.email?.includes("@")) {
        errors.push(`Row ${index + 1}: Invalid email format`);
      }
      if (!row.first_name || !row.last_name) {
        errors.push(`Row ${index + 1}: Missing name`);
      }

      // Validate category values
      categories.forEach((category) => {
        const value = row[category.name];
        if (value) {
          const validValues = validCategoryValues.get(category.name);
          if (!validValues.has(value)) {
            errors.push(
              `Row ${index + 1}: Invalid value "${value}" for ${
                category.name
              }. Valid values are: ${Array.from(validValues).join(", ")}`
            );
          }
        } else {
          warnings.push(`Row ${index + 1}: Missing value for ${category.name}`);
        }
      });
    });

    return { errors, warnings };
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const { errors, warnings } = validateEmployeeData(results.data);
          setValidationErrors(errors);
          setValidationWarnings(warnings);
          setCsvData(results.data);
          setPreviewData(results.data.slice(0, 5));
        },
      });
    }
  };

  const handleBulkImport = async () => {
    if (validationErrors.length > 0 || selectedFacilities.length === 0) return;

    setImporting(true);
    try {
      for (const employee of csvData) {
        // Create employee data without facility_id
        const employeeData = {
          first_name: employee.first_name,
          last_name: employee.last_name,
          email: employee.email,
          position: employee.position,
          company_id: user.company_id,
          id_code: `EMP${Math.random()
            .toString(36)
            .substr(2, 6)
            .toUpperCase()}`,
          is_active: true,
        };

        // Insert the employee
        const { data: newEmployee, error } = await supabase
          .from("users")
          .insert(employeeData)
          .select()
          .single();

        if (error) throw error;

        // Add user-facility relationships
        if (selectedFacilities.length > 0) {
          const facilityEntries = selectedFacilities.map((facilityId) => ({
            user_id: newEmployee.id,
            facility_id: facilityId,
            is_active: true,
          }));

          const { error: facilityError } = await supabase
            .from("user_facilities")
            .insert(facilityEntries);

          if (facilityError) throw facilityError;
        }

        // Add category values
        for (const category of categories) {
          const optionValue = employee[category.name];
          if (optionValue) {
            const option = category.custom_category_options.find(
              (opt) => opt.value === optionValue
            );

            if (option) {
              await supabase.from("user_category_values").insert({
                user_id: newEmployee.id,
                category_id: category.id,
                option_id: option.id,
              });
            }
          }
        }
      }

      onSubmit?.();
      setCsvData([]);
      setValidationErrors([]);
      setValidationWarnings([]);
      setPreviewData([]);
      setSelectedFacilities([]);
    } catch (error) {
      console.error("Import error:", error);
      setValidationErrors([
        ...validationErrors,
        "Import failed: " + error.message,
      ]);
    } finally {
      setImporting(false);
    }
  };

  const ImportSummary = () => (
    <div className="space-y-4 mb-4">
      {validationWarnings.length > 0 && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <Info className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-700">
            <div className="font-medium mb-2">
              Warnings ({validationWarnings.length}):
            </div>
            <ScrollArea className="h-24">
              <ul className="list-disc pl-4 space-y-1">
                {validationWarnings.map((warning, i) => (
                  <li key={i} className="text-sm">
                    {warning}
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </AlertDescription>
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium mb-2">
              Errors ({validationErrors.length}):
            </div>
            <ScrollArea className="h-24">
              <ul className="list-disc pl-4 space-y-1">
                {validationErrors.map((error, i) => (
                  <li key={i} className="text-sm">
                    {error}
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </AlertDescription>
        </Alert>
      )}

      {previewData.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Ukážka dát:</h3>
          <ScrollArea className="h-[400px] border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  {Object.keys(previewData[0]).map((header) => (
                    <TableHead key={header} className="whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{header}</span>
                        {validCategoryValues.has(header) && (
                          <Badge variant="outline" className="bg-gray-100/50">
                            Kategória
                          </Badge>
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewData.map((row, i) => (
                  <TableRow key={i}>
                    {Object.entries(row).map(([key, value], j) => (
                      <TableCell key={j} className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          {validCategoryValues.has(key) && (
                            <Badge
                              variant="outline"
                              className={
                                validCategoryValues.get(key).has(value)
                                  ? "bg-green-50 text-green-700"
                                  : "bg-red-50 text-red-700"
                              }
                            >
                              {validCategoryValues.get(key).has(value)
                                ? "Správne"
                                : "Nesprávne"}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <Tabs defaultValue="single" value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="single">Jednotlivo</TabsTrigger>
          <TabsTrigger value="bulk">Hromadne</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="pt-4">
          <AdminRegistrationForm
            onSuccess={() => {
              if (typeof onSubmit === "function") {
                onSubmit();
              }
            }}
            facilities={facilities}
            categories={categories}
            supabase={supabase}
          />
        </TabsContent>

        <TabsContent value="bulk" className="space-y-6">
          <div className="h-[60vh] pr-4 overflow-y-auto">
            <div className="flex flex-col space-y-4">
              <Label className="text-sm font-medium">
                Vyberte skupiny pre import
              </Label>
              <ScrollArea className="h-[200px] border rounded-md p-4">
                {facilities.map((facility) => (
                  <div
                    key={facility.id}
                    className="flex items-center space-x-2 py-2"
                  >
                    <Checkbox
                      id={`facility-${facility.id}`}
                      checked={selectedFacilities.includes(facility.id)}
                      onCheckedChange={(checked) => {
                        setSelectedFacilities((prev) =>
                          checked
                            ? [...prev, facility.id]
                            : prev.filter((id) => id !== facility.id)
                        );
                      }}
                    />
                    <Label
                      htmlFor={`facility-${facility.id}`}
                      className="flex items-center"
                    >
                      <Building2 className="w-4 h-4 mr-2 text-gray-500" />
                      {facility.name}
                    </Label>
                  </div>
                ))}
              </ScrollArea>
            </div>

            {selectedFacilities.length > 0 && (
              <div className="flex justify-between items-center border-t pt-4 mt-4">
                <Button variant="outline" onClick={generateSampleCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Stiahnuť vzor CSV
                </Button>

                <div>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                    disabled={selectedFacilities.length === 0}
                  />
                  <Label htmlFor="csv-upload">
                    <Button variant="outline" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Nahrať CSV súbor
                      </span>
                    </Button>
                  </Label>
                </div>
              </div>
            )}

            <ImportSummary />

            {csvData.length > 0 && (
              <div className="flex items-center justify-end gap-4 border-t pt-4">
                <span className="text-sm text-gray-500">
                  {csvData.length} zamestnancov na import
                </span>
                <Button
                  onClick={handleBulkImport}
                  disabled={
                    importing ||
                    validationErrors.length > 0 ||
                    selectedFacilities.length === 0
                  }
                >
                  {importing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {importing ? "Importujem..." : "Importovať zamestnancov"}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkEmployeeImport;
